import { useState } from "react";
import { HandHeart, Send } from "lucide-react";
import { api, apiError } from "@/lib/helpers";
import { VOLUNTEER_ROLES } from "@/lib/data";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

export default function Volunteer() {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Animal Care Crew", availability: "", experience: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-gold focus:border-gold";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("We need your name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/volunteers", form);
      toast.success("Welcome to the crew (almost)!", { description: "Our volunteer coordinator will email you about the next orientation." });
      setForm({ name: "", email: "", phone: "", role: "Animal Care Crew", availability: "", experience: "", message: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <section id="volunteer" className="relative bg-sand py-20 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] rounded-tr-[6rem] shadow-2xl shadow-pine/20">
                <img src="/sections/volunteer_dogs.webp"
                  alt="The sanctuary dogs lined up along the deck railing" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-2 sm:right-6 rounded-2xl bg-pine p-5 text-sand shadow-xl max-w-[220px]">
                <HandHeart className="h-6 w-6 text-gold-soft" />
                <p className="mt-2 font-serif text-lg leading-snug">You can change a whole life from anywhere in the world.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <p className="eyebrow">Volunteer</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal tracking-tight">
              Hands that help, hearts that stay.
            </h2>
            <p className="mt-4 max-w-xl text-bark">
              You can support our mission from anywhere in the world by organizing remote fundraisers to help with operational
              costs or by running virtual awareness campaigns to share our animals' stories. You can also coordinate supply
              drives for needed goods or contribute digital skills like graphic design and content creation to boost our visibility.
            </p>

            <form onSubmit={submit} data-testid="volunteer-form" className="mt-8 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 shadow-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="volunteer-name-input" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="volunteer-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="volunteer-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                <select data-testid="volunteer-role-select" value={form.role} onChange={set("role")} className={inputCls}>
                  {VOLUNTEER_ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
                <input data-testid="volunteer-availability-input" placeholder="Availability (e.g. weekends)" value={form.availability} onChange={set("availability")} className={`${inputCls} sm:col-span-2`} />
                <textarea data-testid="volunteer-experience-input" rows={3} placeholder="Anything you'd like us to know?" value={form.experience} onChange={set("experience")} className={`${inputCls} sm:col-span-2`} />
              </div>
              <button data-testid="volunteer-submit-btn" type="submit" disabled={busy}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-bold text-charcoal transition-all hover:bg-gold-soft hover:scale-[1.01] active:scale-95 disabled:opacity-60">
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Join the volunteer crew"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
